import React, { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Task } from '../../types';
import { KanbanColumn } from './KanbanColumn';
import { TaskCard } from '../Tasks/TaskCard';

interface KanbanBoardProps {
  tasks: Task[];
  onMoveTask: (taskId: string, newStatus: Task['status'], position: number) => void;
  onTaskClick: (task: Task) => void;
  onAddTask: (status: Task['status']) => void;
}

const columns: { id: Task['status']; title: string; color: string }[] = [
  { id: 'todo', title: 'Não iniciado', color: '#C24914' },
  { id: 'in_progress', title: 'Em Andamento', color: '#FDAB3D' },
  { id: 'in_review', title: 'Parado', color: '#E2445C' },
  { id: 'done', title: 'Feito', color: '#00C875' },
];

export function KanbanBoard({ tasks, onMoveTask, onTaskClick, onAddTask }: KanbanBoardProps) {
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const getTasksByStatus = (status: Task['status']) =>
    tasks.filter(t => t.status === status).sort((a, b) => a.position - b.position);

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find(t => t.id === event.active.id);
    if (task) setActiveTask(task);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveTask(null);
    const { active, over } = event;
    if (!over) return;

    const draggedTask = tasks.find(t => t.id === active.id);
    if (!draggedTask) return;

    const overData = over.data.current as
      | { type?: 'column'; status?: Task['status'] }
      | { type?: 'task'; status?: Task['status']; task?: Task }
      | undefined;
    const overTask = tasks.find(t => t.id === over.id);
    const newStatus =
      overData?.status ||
      overTask?.status ||
      columns.find(c => c.id === over.id)?.id ||
      draggedTask.status;
    const columnTasks = getTasksByStatus(newStatus).filter(task => task.id !== draggedTask.id);
    const overTaskIndex = overTask ? columnTasks.findIndex(task => task.id === overTask.id) : -1;
    const newPosition = overTaskIndex >= 0 ? overTaskIndex : columnTasks.length;

    if (draggedTask.status !== newStatus || draggedTask.position !== newPosition) {
      onMoveTask(draggedTask.id, newStatus, newPosition);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 min-h-[calc(100vh-200px)]">
        {columns.map(column => {
          const columnTasks = getTasksByStatus(column.id);
          return (
            <SortableContext
              key={column.id}
              items={columnTasks.map(t => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <KanbanColumn
                id={column.id}
                title={column.title}
                color={column.color}
                tasks={columnTasks}
                onTaskClick={onTaskClick}
                onAddTask={() => onAddTask(column.id)}
              />
            </SortableContext>
          );
        })}
      </div>

      <DragOverlay>
        {activeTask && (
          <div className="opacity-90 rotate-2 shadow-xl">
            <TaskCard task={activeTask} onClick={() => {}} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
